import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  IJeopardy,
  IQuote,
  IServiceResponseError,
  InsuranceQuotePhase,
  InsuranceQuoteType,
  QuoteTypeCodes,
  INameCodeWithDefault,
  PaymentFrequency,
} from '@/interfaces';
import { InsuranceQuoteService as _iqService } from '@/services/store/insurance-quote.service';
import { CarService as _iqmqService } from '@/services/store/car.service';
import { cloneDeep, isArray } from 'lodash-es';
import { useJeopardyStore } from '../jeopardy/jeopardy.state';
import { dateToDateString } from '@/utils/data-transforms';
import { JP_GENERIC_CODE, JP_RIDE_SHARE } from '@/constants/jeopardy-codes.const';
import { useReferenceDataStore } from '../reference-data/reference-data.state';
import { useIQOriginalQuoteStore } from '../insurance-quote/original-quote.state';
import { IQMQStepIds } from '@/constants/car-quote.const';
import { useFormStore } from '../form/formStore';
import { IConfig } from '@/interfaces/common/config.interface';
import { QuoteChangeState } from '@/enums/quote-change-state.enum';
import { mapTransmission } from '@/mappers/find-car-mapper';

export const useCarStore = defineStore('Car', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const jpStore = useJeopardyStore();
  const refStore = useReferenceDataStore();
  const originalQuoteStore = useIQOriginalQuoteStore();
  const formStore = useFormStore();
  const quote = ref<Partial<IQuote>>({});
  const quoteId = computed<string>(() => quote.value.id ?? '');
  const quoteType = computed<string>(() => (quote.value.quoteType && QuoteTypeCodes[quote.value.quoteType]) || '');
  const jeopardies = ref<IJeopardy[]>([]);
  const errors = ref<IServiceResponseError[]>([]);
  const excesses = ref<Record<string, INameCodeWithDefault[]>>({});
  const maxEmailSendNumber = ref(5);
  const emailSendCount = ref(0);
  const isResume = ref(false);
  const keepThisCoverSelected = ref(false);
  const excessChangedByCalculation = ref(false);

  const makeAndModel = computed<string>(() => {
    const details = quote.value.motorQuote?.vehicleDetails?.glassesGuideVehicleDetails;
    if (!details) return '';
    return `${details.make?.description ?? ''} ${details.model?.description ?? ''} ${quote.value.motorQuote?.vehicleDetails?.vehicleRegistration?.value?.toUpperCase() ?? ''}`;
  });
  const yearBody = computed(() => {
    const details = quote.value.motorQuote?.vehicleDetails?.glassesGuideVehicleDetails;
    if (!details) return '';
    return `${details.manufacturingYear.year ?? ''} ${details.body.description ?? ''} ${details.option.series ?? ''} ${details.option.doors + 'D'} ${mapTransmission(details.option.transmission)}`;
  });

  const isMC = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorComprehensive;
  });

  const isMT = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorThirdPartyLiability;
  });

  const isMF = computed(() => {
    return quote.value.quoteType == InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability;
  });

  const policyStartDateHasPassed = computed(() => {
    if (quote.value.insuranceStartDate?.value) {
      const today = new Date();
      const quoteStartDate = quote.value.insuranceStartDate.value;
      quoteStartDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return quoteStartDate < today;
    }
    return false;
  });

  const showHireCarOption = computed(() => {
    const rules = quote.value.motorQuote?.quoteOptions.ruleCalculations;
    return rules?.isAllowedToSelectCarHire;
  });

  const showWindscreenOption = computed(() => {
    const rules = quote.value.motorQuote?.quoteOptions.ruleCalculations;
    return rules?.isAllowedToSelectWindscreenProtection;
  });

  const showNCDProtectionOption = computed(() => {
    return quote.value.motorQuote?.primaryDriverDetails?.ruleCalculations?.isAllowedToSelectNcdProtectionOption;
  });

  // ------------------------------------------------------------- //
  // Define Actions
  // ------------------------------------------------------------- //
  async function createQuote(quoteType: InsuranceQuoteType) {
    quote.value.quoteType = quoteType;
    quote.value.quotePhase = InsuranceQuotePhase.QuickQuote;
    quote.value.renewalPreference = true;
    quote.value.renewalTermAcceptance = true;
    quote.value.paymentFrequency = PaymentFrequency.Monthly;
    const insuranceStartDate = quote.value.insuranceStartDate;
    const res = await _iqService.createQuote(quote.value);
    quote.value = {
      ...res.quote,
      insuranceStartDate,
    };
    // refresh quote count when crete a new quote;
    const settings = await refStore.getIQConfigSettings();
    maxEmailSendNumber.value = Number(settings.find((item: IConfig) => item.name == 'MaxEmailSendNumber')?.value ?? 5);
  }

  async function cloneQuote(quoteType: InsuranceQuoteType, lowValueVehicle: boolean = false) {
    const insuranceStartDate = quote.value.insuranceStartDate;
    const quoteToClone = {
      quoteType,
      quotePhase: InsuranceQuotePhase.QuickQuote,
      renewalTermAcceptance: true,
      renewalPreference: true,
      insuranceStartDate,
    };
    const previousQuote = cloneDeep(quote.value);
    const res = await _iqService.createQuote(quoteToClone);
    quote.value = {
      ...res.quote,
      ...previousQuote,
      insuranceStartDate,
      quoteType,
      quotePhase: InsuranceQuotePhase.QuickQuote,
      id: res.quote.id,
      reference: res.quote.reference,
      motorQuote: {
        ...previousQuote.motorQuote!,
        coverageId: res.quote.motorQuote.coverageId,
        coverageType: res.quote.motorQuote.coverageType,
        coverageDiscounts: res.quote.motorQuote.coverageDiscounts,
        discounts: res.quote.motorQuote.discounts,
        quoteOptions: res.quote.motorQuote.quoteOptions,
        standardExcess: res.quote.motorQuote.standardExcess,
        standardExcessCode: res.quote.motorQuote.standardExcessCode,
        vehicleLocationDetails: lowValueVehicle
          ? previousQuote.motorQuote!.vehicleLocationDetails
          : res.quote.motorQuote.vehicleLocationDetails,
      },
      discounts: res.quote.discounts,
    };

    quote.value.motorQuote!.quoteOptions.vehicleSumInsuredValue = {
      state: QuoteChangeState.Changed,
    };
    quote.value.motorQuote!.quoteOptions.agreedVehicleValue = {
      state: QuoteChangeState.Changed,
    };
    if (isMF.value) {
      quote.value.motorQuote!.quoteOptions.vehicleSumInsuredValue = {
        value: quote.value.motorQuote!.vehicleDetails.glassesGuideVehicleDetails.vehicleValue.value,
        state: QuoteChangeState.Changed,
      };
    }
  }

  async function getMotorExcess() {
    if (excesses.value[quoteType.value + quote.value.insuranceStartDate?.value])
      return excesses.value[quoteType.value + quote.value.insuranceStartDate?.value];
    const value = await _iqmqService.getMotorExcess(
      quoteType.value,
      quote.value.insuranceStartDate?.value
        ? dateToDateString(quote.value.insuranceStartDate?.value, 'yyyy-MM-dd')
        : undefined
    );
    excesses.value[quoteType.value + quote.value.insuranceStartDate?.value] = value;
    return value;
  }

  /**
   * Apply Business Rules
   *
   * @param formData
   */
  const applyBusinessRules = async () => {
    try {
      const response = await _iqService.applyBusinessRules(quoteId.value, quote.value);
      quote.value = response?.quote || quote.value;

      await handleJeopardyOrReasonCodes(response);
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Apply Business Rules
   *
   * @param formData
   */
  const saveQuote = async () => {
    try {
      const response = await _iqService.saveQuote(quoteId.value, quote.value);
      quote.value = response?.quote || quote.value;

      await handleJeopardyOrReasonCodes(response);
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Calculate quote price
   *
   * @returns
   */
  const calculatePrice = async () => {
    try {
      const response = await _iqService.calculatePrice(quoteId.value, quote.value);
      // Check if excess code changed, this happens when we change default excess code for MF and MT
      excessChangedByCalculation.value =
        response?.quote?.motorQuote?.standardExcessCode?.value != quote.value.motorQuote?.standardExcessCode?.value;

      // Update local quote store
      quote.value = response?.quote || quote.value;

      await handleJeopardyOrReasonCodes(response);
    } catch (error) {
      await handleError(error);
    }
  };

  const handleJeopardyOrReasonCodes = async (response: any) => {
    errors.value = [];
    jeopardies.value = [];

    if (response.success && response.data?.jeopardies?.length) {
      jeopardies.value = response.data.jeopardies;
      throw response.data.jeopardies[0];
    }

    if (response?.reasonCodes?.length > 0) {
      errors.value = response?.reasonCodes;
      throw response.reasonCodes[0];
    }

    if (response?.errors?.length > 0) {
      errors.value = [response.errors[0]];
      throw response.errors[0].errorType;
    }
  };

  const handleError = async (error: any) => {
    const isErrorArray = isArray(error);
    errors.value = isErrorArray ? error : [error];
    if (!isErrorArray && error?.code) {
      throw error.code;
    }

    throw ['Unknown error'];
  };

  const applyRulesAndSave = async (
    calculateQuotePrice: boolean = false,
    showJeopardyPanel: boolean = true,
    restoreQuoteIfJeopardy = false
  ) => {
    try {
      restoreQuoteIfJeopardy && originalQuoteStore.setQuote(quote.value);
      quote.value.quotePhase = InsuranceQuotePhase.QuickQuote;
      // Apply business rules
      await applyBusinessRules();

      if (calculateQuotePrice && !errors.value?.length) {
        // Save Quote for Pricing
        await saveQuote();

        // Recalculate quote price
        await calculatePrice();
      }

      // Save quote with new price
      await saveQuote();
    } catch (error) {
      restoreQuoteIfJeopardy && (quote.value = cloneDeep(originalQuoteStore.quote!));
      showJeopardyPanel &&
        jpStore.showJeopardyPanel({
          jpCode: jeopardies.value[0]?.code || (error as string),
          areNextStepsVisible: false,
        });
      throw error;
    }
  };

  const showRideSharingJeopardy = () => {
    jpStore.showJeopardyPanel({
      jpCode: JP_RIDE_SHARE,
      areNextStepsVisible: false,
    });
  };

  /**
   * Send Quote Email
   *
   * @param formData
   */
  const sendQuoteEmail = async (destinationAddress: string) => {
    if (!maxEmailSendNumber.value) return;
    await _iqService.sendQuoteEmail(quoteId.value, destinationAddress);
    maxEmailSendNumber.value -= 1;
  };

  const fetchQuote = async () => {
    try {
      // get quote id
      formStore.fullScreenLoading = true;
      const params = new URLSearchParams(window.location.search);
      const quoteId = params.get('ref') ?? '';
      if (!quoteId) return;
      const res = await _iqService.fetchQuote(quoteId);
      quote.value = res?.quote;
      if (quote.value.policyReference || quote.value.paymentDueDate) throw JP_GENERIC_CODE; // throw error if quote already bound
      originalQuoteStore.setQuote(quote.value);
      isResume.value = !!quote.value;
      return resumeExistingQuote().then(formStore.hideFullScreenSpinner);
    } catch (error) {
      console.error(error);
    } finally {
      !isResume.value && (formStore.fullScreenLoading = false);
    }
  };

  const getSessionId = () => {
    return _iqService.getSessionId(quote.value.id!);
  };

  const resumeExistingQuote = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      let step = params.get('step');
      if (step) {
        if (policyStartDateHasPassed.value) {
          step = IQMQStepIds.StartYourQuote;
        } else {
          await applyRulesAndSave(false);
        }
        formStore.fastForwardToStepId(step);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchExistingPolicies = async () => {
    const res = await _iqService.fetchMemberInsurancePolicies();
    return res;
  };

  return {
    isMC,
    isMT,
    isMF,
    quote,
    quoteId,
    quoteType,
    makeAndModel,
    yearBody,
    policyStartDateHasPassed,
    isResume,
    showHireCarOption,
    showWindscreenOption,
    showNCDProtectionOption,
    emailSendCount,
    keepThisCoverSelected,
    excessChangedByCalculation,
    fetchQuote,
    getSessionId,
    createQuote,
    cloneQuote,
    getMotorExcess,
    applyRulesAndSave,
    showRideSharingJeopardy,
    sendQuoteEmail,
    fetchExistingPolicies,
  };
});
